/**
 * Typed mirror of `tokens.css`. Use in TS when a value needs to flow
 * into JSX (e.g. inline accent on a tile). Component CSS should prefer
 * `var(--c-*)` / `var(--hm-*)`; this module is a fallback, not the
 * primary surface.
 *
 * Source of truth: `web/src/styles/tokens.css` (the "Woven Signal ·
 * BLUE INK" Amassa identity, 2026-08-10 revision). Keep these hexes in
 * lockstep with the `--c-*` values there.
 *
 * ⚠ THE ACCENTS ARE DUAL-VALUED. As of the blue-ink revision the 15
 * module accents, the brand, the signal cyan and the heat red each have a
 * dark value (`:root`) and a light one (`[data-theme='light']`).
 *
 * An INLINE style wins the cascade over `[data-theme='light']`, so a JS
 * consumer that writes a raw hex pins ONE theme's colour into both — which
 * is exactly what happened: `style={{'--accent': moduleAccent.restools}}`
 * painted the dark #6FA8FF onto the ivory ground (2.84:1 → 2.24:1) across
 * ~129 files, because the CSS light override never got a chance to run.
 *
 * So `moduleAccent` / `moduleAccentRgb` no longer hold hexes at all: they
 * hold `var(--c-*)` REFERENCES. Writing one inline is theme-correct by
 * construction — the custom property resolves against `<html>`, where the
 * theme lives, at the element that declares it. The literal hexes are
 * still available as `moduleAccentDark` / `moduleAccentLight` (and via
 * `accentFor`) for the rare consumer that must compute on the value.
 *
 * 15 module accents woven around the orange brand + cyan signal, all
 * re-plotted against the blue-slate ink ground (AA floor 6.95:1 on
 * `--bg`); a single Mona Sans typeface (body/display/labels); soft
 * rounded radii (10/12/14/18/26/999).
 */

import type { ModuleSlug } from '@/shared/modules/ids';
import { DEFAULT_MODULE_TREE } from '@/shared/modules/catalog';
import { deriveModuleAccent, deriveModuleAccentRgb } from '@/shared/modules/registries';

/** A built-in shell module's route slug. Spelled in `shared/modules/ids.ts`. */
export type { ModuleSlug };

export const radius = {
  sm: '10px',
  md: '12px',
  lg: '18px',
  /** Default surface radius (cards/modals/table-wrapper). */
  default: '14px',
  hero: '26px',
  pill: '999px',
} as const;

/**
 * Module-slug → accent, as a CSS custom-property REFERENCE.
 *
 * This is the map to use anywhere the value ends up in CSS — an inline
 * style, a `setProperty`, an SVG `fill`/`stroke`, a `color-mix(...)`
 * string. It resolves per theme (and per `[data-theme]` toggle, live,
 * with no re-render) because the browser does the lookup, not us.
 *
 * Slug keys match the React route paths; the CSS names are the shorter
 * `--c-*` spellings declared in `tokens.css` (§2 dark, §2b light). Derived
 * from each module's `ink` in `shared/modules/catalog.json`.
 *
 * Need the literal hex (a gradient you must compute, a canvas fill, a
 * value you slice)? Use `accentFor(slug, theme)` — never these.
 */
export const moduleAccent: Record<ModuleSlug, string> = deriveModuleAccent(DEFAULT_MODULE_TREE);

/**
 * Module-slug → accent hex, DARK theme. Mirrors the `--c-*` block in
 * `tokens.css` `:root`. Prefer `moduleAccent` (theme-resolving) unless
 * you genuinely need the literal.
 */
export const moduleAccentDark = {
  coportal:    '#FFC91C',
  restools:    '#6FA8FF',
  lemax:       '#FF6E62',
  contracting: '#F5B043',
  hr:          '#FF9455',
  it:          '#D19A5A',
  finance:     '#8FD0FF',
  data:        '#AC9BFF',
  ecommerce:   '#F27ED6',
  bizdev:      '#B8A845',
  product:     '#FF8FAE',
  management:  '#E8B48A',
  aiaiai:      '#C48EFF',
  admin:       '#FF8A14',
  preferences: '#D8C48F',
  'guest-portal': '#C9D162',
} as const;

/**
 * Module-slug → accent hex, LIGHT theme. These are the pre-blue-ink
 * values, kept verbatim so the ivory theme is unchanged. Mirrors the
 * `--c-*` block in `tokens.css` `[data-theme='light']`.
 */
export const moduleAccentLight = {
  coportal:    '#D9A41C',
  restools:    '#5A93F0',
  lemax:       '#F0645A',
  contracting: '#E8A53C',
  hr:          '#F2864A',
  it:          '#A87438',
  finance:     '#6FB0F5',
  data:        '#9B8CF5',
  ecommerce:   '#E06BC4',
  bizdev:      '#8F8230',
  product:     '#F27EA0',
  management:  '#C98F5E',
  aiaiai:      '#B57DF0',
  admin:       '#F5871A',
  preferences: '#B5A06A',
  'guest-portal': '#6E7612',
} as const;

/** Amassa brand constants — the warp (orange) and the signal (cyan). DARK. */
export const brand = {
  orange: '#FF8A14',
  orangeBright: '#FFAA4D',
  orangeDeep: '#C06A0A',
  cyan: '#FFDC6B',
  cyanBright: '#FFECA8',
  red: '#F4505A',
  /** Ink on a SOLID orange / cyan / red fill. Never white — 2.85:1. */
  accentInk: '#0A0E18',
} as const;

/** Amassa brand constants, LIGHT theme (unchanged by the blue-ink pass). */
export const brandLight = {
  orange: '#F5871A',
  orangeBright: '#C96A14',
  orangeDeep: '#9E5C0A',
  cyan: '#C99A24',
  cyanBright: '#E0B23A',
  red: '#E5484D',
  /** Ink on a SOLID orange / red fill. Was '#ffffff' — 2.86:1 on the
   *  brand, i.e. the exact pairing the dark theme bans. 7.4:1. */
  accentInk: '#2A1405',
} as const;


/**
 * Module-slug → accent RGB triple, as a CSS custom-property REFERENCE —
 * the `rgba(${ARGB}, 0.22)` companion to `moduleAccent`. Theme-resolving
 * for the same reason: the browser reads `--c-*-rgb` at the element.
 */
export const moduleAccentRgb: Record<ModuleSlug, string> = deriveModuleAccentRgb(DEFAULT_MODULE_TREE);

/**
 * RGB triples for `rgba(...)` / `color-mix(...)` usage where a hex
 * literal isn't enough. DARK theme (see `moduleAccentRgbLight`).
 */
export const moduleAccentRgbDark = {
  coportal:    '255, 201, 28',
  restools:    '111, 168, 255',
  lemax:       '255, 110, 98',
  contracting: '245, 176, 67',
  hr:          '255, 148, 85',
  it:          '209, 154, 90',
  finance:     '143, 208, 255',
  data:        '172, 155, 255',
  ecommerce:   '242, 126, 214',
  bizdev:      '184, 168, 69',
  product:     '255, 143, 174',
  management:  '232, 180, 138',
  aiaiai:      '196, 142, 255',
  admin:       '255, 138, 20',
  preferences: '216, 196, 143',
  'guest-portal': '201, 209, 98',
} as const;

/** RGB triples, LIGHT theme (pre-blue-ink values, unchanged). */
export const moduleAccentRgbLight = {
  coportal:    '217, 164, 28',
  restools:    '90, 147, 240',
  lemax:       '240, 100, 90',
  contracting: '232, 165, 60',
  hr:          '242, 134, 74',
  it:          '168, 116, 56',
  finance:     '111, 176, 245',
  data:        '155, 140, 245',
  ecommerce:   '224, 107, 196',
  bizdev:      '143, 130, 48',
  product:     '242, 126, 160',
  management:  '201, 143, 94',
  aiaiai:      '181, 125, 240',
  admin:       '245, 135, 26',
  preferences: '181, 160, 106',
  'guest-portal': '110, 118, 18',
} as const;

/**
 * Theme-aware LITERAL lookup — the escape hatch for the rare consumer
 * that must compute on the value rather than hand it to CSS (canvas, an
 * image encoder, a colour-maths helper).
 *
 * For anything that lands in a stylesheet or an inline style, prefer
 * `moduleAccent` / `moduleAccentRgb`: they resolve per theme at paint
 * time, so they can't go stale when the user flips the toggle.
 */
export function accentFor(
  slug: ModuleSlug,
  theme: 'dark' | 'light' = 'dark',
): { hex: string; rgb: string } {
  return theme === 'light'
    ? { hex: moduleAccentLight[slug], rgb: moduleAccentRgbLight[slug] }
    : { hex: moduleAccentDark[slug], rgb: moduleAccentRgbDark[slug] };
}

export const font = {
  body: '"Mona Sans", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  /** Label face — Mona Sans set uppercase + tracked (NOT a monospace). */
  mono: '"Mona Sans", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  serif: '"Mona Sans", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  display: '"Mona Sans", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  /** OS monospace — raw code / IDs only; no webfont downloaded. */
  code: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
} as const;
